import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { AppNode, LightSourceParams, LightingParams, NodeStatus } from '@/types';
import { useModelStore } from '@/stores/modelStore';
import { getModelCost } from '@/utils/credits';
import { InverseScaleBox } from '../InverseScaleBox';
import { Slider } from '@/components/director/ui/Slider';
import { RotateCcw, Lightbulb, Plus, X } from 'lucide-react';
import { LightingViewport } from './LightingViewport';
import {
  LIGHTING_PRESETS,
  MAX_LIGHTS,
  DEFAULT_LIGHTING_PARAMS,
  clampHorizontalAngle,
  clampVerticalAngle,
  clampIntensity,
  normalizeHex,
  initLightingParams,
  normalizeLight,
  makeLightLabel,
  LightingPreset,
} from './lightingPresets';
import { buildLightingParams, LightingRequestParams } from './buildLightingParams';
import { COLOR_SWATCHES } from './colorSwatches';

const ACCENT = '#6D5BF5';
// 打灯生成固定使用 fal.ai Relighting 模型（用户无需选择模型）
const GENERATE_MODEL_ID = 'fal-image-apps-v2-relighting';

interface LightingPanelProps {
  data: AppNode;
  cardHeight: number;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => void;
  onGenerateLighting?: (sourceNodeId: string, params: {
    imageUrl: string;
    model?: string;
    lightingParams: LightingParams;
    lightingRequest: LightingRequestParams;
  }) => void;
  onClose: () => void;
}

const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 2L10 10M10 2L2 10" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const PresetChip = memo<{ label: string; active: boolean; onClick: () => void }>(({ label, active, onClick }) => (
  <button
    onMouseDown={(e) => e.stopPropagation()}
    onClick={onClick}
    className={`h-7 px-1.5 rounded-md text-[11px] truncate cursor-pointer border transition-colors ${
      active
        ? 'text-white border-white/25 bg-[#2c2c2e]'
        : 'bg-white/5 text-white/55 border-transparent hover:text-white/85 hover:bg-[#3a3a3d]'
    }`}
    style={active ? { background: ACCENT, borderColor: ACCENT } : undefined}
    title={label}
  >
    {label}
  </button>
));
PresetChip.displayName = 'PresetChip';

const SectionLabel = memo<{ text: string; valueText?: string }>(({ text, valueText }) => (
  <div className="flex items-center justify-between">
    <span className="text-[11px] text-white/80 shrink-0">{text}</span>
    {valueText !== undefined && <span className="text-[11px] text-white/50 font-mono tabular-nums">{valueText}</span>}
  </div>
));
SectionLabel.displayName = 'SectionLabel';

const Divider = () => <div className="h-px bg-white/10 shrink-0" />;

export const LightingPanel: React.FC<LightingPanelProps> = ({
  data,
  cardHeight,
  onNodeUpdate,
  onGenerateLighting,
  onClose,
}) => {
  const imageUrl = data.data.image;
  const isWorking = data.status === NodeStatus.WORKING;
  // 积分系统 P1：固定模型定价预估（未配置 = 0）
  const models = useModelStore((s) => s.models);

  const [params, setParams] = useState<LightingParams>(() => initLightingParams(data.data.lightingParams));
  const { presetId = 'custom', sceneRotation = 0, lights = [], activeLightId = 'main' } = params;
  const activeLight = lights.find((l) => l.id === activeLightId) || lights[0];

  const paramsRef = useRef(params);
  paramsRef.current = params;
  const onNodeUpdateRef = useRef(onNodeUpdate);
  onNodeUpdateRef.current = onNodeUpdate;

  // 关闭面板时将最后使用的参数持久化到节点 data（再次打开恢复、随撤销回退）
  useEffect(() => {
    return () => {
      onNodeUpdateRef.current(data.id, { lightingParams: JSON.parse(JSON.stringify(paramsRef.current)) });
    };
  }, [data.id]);

  /** 手动改任意参数 → 预设态转「自定义」（方案 §3：含增删灯） */
  const markCustom = useCallback((prev: LightingParams): LightingParams => ({ ...prev, presetId: 'custom' }), []);

  const updateLight = useCallback((lightId: string, patch: Partial<LightSourceParams>) => {
    setParams((prev) => ({
      ...markCustom(prev),
      lights: (prev.lights || []).map((l) => (l.id === lightId ? { ...l, ...patch } : l)),
    }));
  }, [markCustom]);

  const handleSceneRotationChange = useCallback((rotation: number) => {
    setParams((prev) => ({ ...prev, sceneRotation: clampHorizontalAngle(rotation) }));
  }, []);

  const handleActiveLightChange = useCallback((lightId: string) => {
    setParams((prev) => ({ ...prev, activeLightId: lightId }));
  }, []);

  /** 视口拖拽灯点回调：水平/垂直角同时更新（绝对值，视口内已 clamp） */
  const handleLightAngleChange = useCallback((lightId: string, horizontal: number, vertical: number) => {
    setParams((prev) => ({
      ...markCustom(prev),
      lights: (prev.lights || []).map((l) =>
        l.id === lightId ? { ...l, horizontalAngle: horizontal, verticalAngle: vertical } : l,
      ),
    }));
  }, [markCustom]);

  const handlePresetClick = useCallback((p: LightingPreset) => {
    setParams((prev) => {
      // 多灯种子预设：一次创建全部灯；普通预设：重置为单盏主灯
      if (p.presetLights && p.presetLights.length > 0) {
        const seeded = p.presetLights.slice(0, MAX_LIGHTS).map((seed, i) =>
          normalizeLight({ ...seed, id: i === 0 ? 'main' : `fill-${i}` }, i, i === 0),
        );
        return { ...prev, presetId: p.id, lights: seeded, activeLightId: seeded[0].id };
      }
      const single = normalizeLight(
        {
          id: 'main',
          label: '主光',
          horizontalAngle: p.horizontalAngle,
          verticalAngle: p.verticalAngle,
          lightType: p.lightType,
          intensity: p.intensity,
          color: p.color,
        },
        0,
        true,
      );
      return { ...prev, presetId: p.id, lights: [single], activeLightId: single.id };
    });
  }, []);

  const handleAddLight = useCallback(() => {
    setParams((prev) => {
      const lights = prev.lights || [];
      if (lights.length >= MAX_LIGHTS) return prev;
      // 找最小可用补光序号复用空位（删除中间灯后再添加，避免与现存 fill-N 撞 id）
      let index = 1;
      while (index <= MAX_LIGHTS && lights.some((l) => l.id === `fill-${index}`)) index++;
      const light = normalizeLight({ id: `fill-${index}`, label: makeLightLabel(index) }, index, false);
      return { ...prev, presetId: 'custom', lights: [...lights, light], activeLightId: light.id };
    });
  }, []);

  const handleRemoveLight = useCallback((lightId: string) => {
    setParams((prev) => {
      const lights = prev.lights || [];
      const target = lights.find((l) => l.id === lightId);
      if (!target || target.isMain || lights.length <= 1) return prev;
      const next = lights.filter((l) => l.id !== lightId);
      const activeStillExists = next.some((l) => l.id === prev.activeLightId);
      return {
        ...prev,
        presetId: 'custom',
        lights: next,
        activeLightId: activeStillExists ? prev.activeLightId : next[0].id,
      };
    });
  }, []);

  const handleReset = useCallback(() => {
    setParams({ ...DEFAULT_LIGHTING_PARAMS, lights: [{ ...DEFAULT_LIGHTING_PARAMS.lights![0] }], sceneRotation: 0 });
  }, []);

  const handleColorTextBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    if (!activeLight) return;
    const normalized = normalizeHex(e.target.value);
    updateLight(activeLight.id, { color: normalized });
  }, [activeLight, updateLight]);

  const handleGenerate = useCallback(() => {
    if (!imageUrl || isWorking || !activeLight) return;
    const lightingRequest = buildLightingParams(paramsRef.current);
    onClose();
    onGenerateLighting?.(data.id, {
      imageUrl,
      model: GENERATE_MODEL_ID,
      lightingParams: JSON.parse(JSON.stringify(paramsRef.current)),
      lightingRequest,
    });
  }, [imageUrl, isWorking, activeLight, onClose, onGenerateLighting, data.id]);

  return (
    <InverseScaleBox
      fixedContent
      fitViewport
      className="absolute left-1/2 z-30"
      style={{
        top: cardHeight + 8,
        transform: 'translateX(-50%)',
        transformOrigin: 'center top',
      }}
      scaleOrigin="center top"
    >
      {/* 流式化（P0）：尺寸加视口上限——宽窗（≥1152px）下与原 1120×504 完全一致；
          窄窗时收缩到视口内（四周边距 32px），杜绝溢出裁切。100vw/100vh 为视口单位，
          不受 InverseScaleBox 祖先 transform 影响，无需 JS 测量 */}
      <div
        className="flex flex-col bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-lg overflow-hidden nodrag nowheel"
        style={{ width: 'min(1120px, 100vw - 32px)', height: 'min(504px, 100vh - 32px)' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* 标题行 */}
        <div className="flex items-center justify-between px-4 h-10 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2">
            <Lightbulb size={14} className="text-white/80" />
            <span className="text-sm font-medium text-white">灯光调节</span>
          </div>
          <button
            onClick={onClose}
            onMouseDown={(e) => e.stopPropagation()}
            className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-white/10 transition-colors shrink-0 cursor-pointer"
          >
            <CloseIcon />
          </button>
        </div>

        {/* 主体：左 3D 视口 + 右参数区（流式化 P1：宽容器下分配与原固定 560 一致；
            窄容器时右侧参数区（flex-1 + overflow-y-auto）先收、左视口最后收且不小于
            360 保底，3D 预览内部 ResizeObserver 随宽自动重渲染） */}
        <div className="flex flex-1 min-h-0 gap-3 px-4 py-2">
          <div className="relative h-full" style={{ flex: '0 1 560px', minWidth: 360 }}>
            <LightingViewport
              imageUrl={imageUrl}
              lights={lights}
              sceneRotation={sceneRotation}
              activeLightId={activeLight?.id || 'main'}
              onLightAngleChange={handleLightAngleChange}
              onSceneRotationChange={handleSceneRotationChange}
              onActiveLightChange={handleActiveLightChange}
            />

            {/* 底部灯光角度栏：当前编辑灯的场景/水平/垂直滑块横排一行（标签→滑轨→数值）
                （浮层半透明；除滑块控件外不拦截指针——避免遮挡底部区域的灯点拖拽，方案 §3） */}
            {activeLight && (
              <div
                className="absolute left-2 right-2 bottom-2 rounded-lg bg-black/55 backdrop-blur-md border border-white/10 pl-2.5 pr-3 py-1.5 flex items-center"
                style={{ pointerEvents: 'none' }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <span className="text-[11px] font-medium text-white/90 shrink-0 mr-2.5">{activeLight.label}</span>
                <div className="flex items-center gap-2 flex-1 min-w-0" style={{ pointerEvents: 'auto' }}>
                  <span className="text-[11px] text-white/55 shrink-0">场景</span>
                  <div className="flex-1 min-w-0">
                    <Slider
                      value={sceneRotation}
                      min={-180}
                      max={180}
                      step={1}
                      onChange={(v) => handleSceneRotationChange(v)}
                      accentColor={ACCENT}
                      valueFormatter={(v) => `${v}°`}
                    />
                  </div>
                </div>
                <div className="w-px h-5 bg-white/10 shrink-0 mx-2.5" />
                <div className="flex items-center gap-2 flex-1 min-w-0" style={{ pointerEvents: 'auto' }}>
                  <span className="text-[11px] text-white/55 shrink-0">水平</span>
                  <div className="flex-1 min-w-0">
                    <Slider
                      value={activeLight.horizontalAngle ?? 0}
                      min={-180}
                      max={180}
                      step={1}
                      onChange={(v) => updateLight(activeLight.id, { horizontalAngle: clampHorizontalAngle(v) })}
                      accentColor={ACCENT}
                      valueFormatter={(v) => `${v}°`}
                    />
                  </div>
                </div>
                <div className="w-px h-5 bg-white/10 shrink-0 mx-2.5" />
                <div className="flex items-center gap-2 flex-1 min-w-0" style={{ pointerEvents: 'auto' }}>
                  <span className="text-[11px] text-white/55 shrink-0">垂直</span>
                  <div className="flex-1 min-w-0">
                    <Slider
                      value={activeLight.verticalAngle ?? 60}
                      min={-20}
                      max={90}
                      step={1}
                      onChange={(v) => updateLight(activeLight.id, { verticalAngle: clampVerticalAngle(v) })}
                      accentColor={ACCENT}
                      valueFormatter={(v) => `${v}°`}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 右侧参数区（~560 宽） */}
          <div className="flex-1 min-w-0 flex flex-col gap-2 overflow-y-auto px-2">
            {/* 灯光预设：18 chips 6列×3行（右区内容宽 ~508px，5 列会溢出裁切） */}
            <div className="flex flex-col gap-1.5">
              <SectionLabel text="灯光预设" valueText={presetId === 'custom' ? '自定义' : '预设'} />
              <div className="grid grid-cols-6 gap-1.5">
                {LIGHTING_PRESETS.map((p) => (
                  <PresetChip key={p.id} label={p.label} active={presetId === p.id} onClick={() => handlePresetClick(p)} />
                ))}
              </div>
            </div>

            <Divider />

            {/* 灯光列表：主光 + 补光 + 添加 */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <SectionLabel text="灯光列表" valueText={`${lights.length}/${MAX_LIGHTS}`} />
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={handleAddLight}
                  disabled={lights.length >= MAX_LIGHTS}
                  title={lights.length >= MAX_LIGHTS ? `最多 ${MAX_LIGHTS} 盏灯` : '添加补光'}
                  className="flex items-center justify-center w-6 h-6 rounded-md bg-white/10 hover:bg-white/20 text-white/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <Plus size={12} />
                </button>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {lights.map((l) => {
                  const active = l.id === activeLight?.id;
                  return (
                    <div key={l.id} className="relative group">
                      <button
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => setParams((prev) => ({ ...prev, activeLightId: l.id }))}
                        className={`flex items-center gap-1.5 h-7 pl-2.5 rounded-md text-[11px] border transition-colors cursor-pointer ${
                          l.isMain ? 'pr-2.5' : 'pr-1.5'
                        } ${
                          active
                            ? 'text-white border-white/25'
                            : 'bg-white/5 text-white/55 border-transparent hover:text-white/85 hover:bg-[#3a3a3d]'
                        }`}
                        style={active ? { background: ACCENT, borderColor: ACCENT } : undefined}
                        title={l.isMain ? '主灯（生成参数由主灯映射，不可删除）' : l.label}
                      >
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ background: l.color || '#FFFFFF', boxShadow: `0 0 4px ${l.color || '#FFFFFF'}` }}
                        />
                        {l.label}
                      </button>
                      {!l.isMain && (
                        <button
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); handleRemoveLight(l.id); }}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#3a3a3d] hover:bg-[#52525b] text-white/70 hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          title={`删除${l.label}`}
                        >
                          <X size={9} strokeWidth={2.5} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <Divider />

            {/* 灯光类型（紧凑固定宽）+ 灯光强度（占余下全宽） */}
            <div className="flex gap-4 items-end">
              <div className="shrink-0 flex flex-col gap-1.5">
                <SectionLabel text="灯光类型" />
                <div className="flex h-7 rounded-md bg-white/5 border border-white/10 overflow-hidden">
                  {(['soft', 'hard'] as const).map((t) => {
                    const active = (activeLight?.lightType ?? 'soft') === t;
                    return (
                      <button
                        key={t}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => activeLight && updateLight(activeLight.id, { lightType: t })}
                        className={`w-12 text-[11px] transition-colors cursor-pointer ${
                          active ? 'text-white' : 'text-white/45 hover:text-white/80'
                        }`}
                        style={active ? { background: ACCENT } : undefined}
                      >
                        {t === 'soft' ? '柔光' : '硬光'}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                <SectionLabel text="灯光强度" valueText={`${activeLight?.intensity ?? 5}`} />
                {/* w-full 内层防 Slider 根节点被 flex 容器按内容收缩（否则导轨 flex-1 塌缩为 0） */}
                <div className="h-7 flex items-center">
                  <div className="w-full">
                    <Slider
                      value={activeLight?.intensity ?? 5}
                      min={0}
                      max={10}
                      step={1}
                      onChange={(v) => activeLight && updateLight(activeLight.id, { intensity: clampIntensity(v) })}
                      accentColor={ACCENT}
                    />
                  </div>
                </div>
              </div>
            </div>

            <Divider />

            {/* 灯光颜色：18 色块 15列网格铺满整行（cell ≈ 28px 近方形）+ 自定义 */}
            <div className="flex flex-col gap-1.5">
              <SectionLabel text="灯光颜色" />
              <div className="grid grid-cols-[repeat(15,minmax(0,1fr))] gap-1.5">
                {COLOR_SWATCHES.map((c) => {
                  const active = (activeLight?.color ?? '#FFFFFF').toUpperCase() === c.toUpperCase();
                  return (
                    <button
                      key={c}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => activeLight && updateLight(activeLight.id, { color: c })}
                      className={`h-7 rounded-md border transition-transform cursor-pointer hover:scale-110 ${active ? 'border-white' : 'border-white/15'}`}
                      style={{ background: c }}
                      title={c}
                    />
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={activeLight?.color ?? '#FFFFFF'}
                  onChange={(e) => activeLight && updateLight(activeLight.id, { color: e.target.value.toUpperCase() })}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="w-7 h-7 rounded-md border border-white/15 bg-transparent cursor-pointer shrink-0"
                  title="自定义颜色"
                />
                <input
                  type="text"
                  defaultValue={activeLight?.color ?? '#FFFFFF'}
                  key={activeLight?.id ?? 'main'}
                  onBlur={handleColorTextBlur}
                  onMouseDown={(e) => e.stopPropagation()}
                  spellCheck={false}
                  maxLength={7}
                  className="w-24 h-7 bg-white/5 border border-white/10 rounded-md px-2 text-[11px] font-mono text-white/90 outline-none focus:border-white/30 uppercase"
                  placeholder="#FFFFFF"
                />
                <span className="text-[10px] text-white/30">自定义色号（仅联动 3D 预览）</span>
              </div>
            </div>
          </div>
        </div>

        {/* 底部操作栏 */}
        <div className="flex items-center justify-between px-4 h-12 border-t border-white/10 shrink-0">
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleReset}
            className="flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] text-white/60 hover:text-white hover:bg-[#3a3a3d] transition-colors cursor-pointer"
          >
            <RotateCcw size={12} />
            重置参数
          </button>
          {/* 右组：积分消耗显示（与节点底部面板一致，单价 = 固定模型定价，未配置 = 0）+ 发送 */}
          <div className="flex items-center gap-3">
            <span
              className="flex items-center gap-1 text-[12px] text-white/40"
              title="本次生成预计消耗积分"
            >
              <img src="/icons/home/token-icon.svg" alt="" width={12} height={12} draggable={false} />
              {/* 预留三位数积分宽度（tabular-nums 等宽数字），位数变化不引起布局跳动 */}
              <span className="min-w-[22px] tabular-nums text-left">{getModelCost(GENERATE_MODEL_ID, models)}</span>
            </span>
            <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleGenerate}
              disabled={!imageUrl || isWorking}
              title={!imageUrl ? '请先生成或上传图片' : isWorking ? '生成中，请稍候' : '生成打光图片'}
              className="flex items-center justify-center w-8 h-8 bg-white rounded-[8px] text-[#1c1c1e] transition-transform hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
            >
              {/* v1.4.11：图标统一为项目 send-arrow.svg（与其他节点底部面板按钮同字形） */}
              <img src="/icons/send-arrow.svg" alt="生成" width={16} height={16} style={{ objectFit: 'contain' }} draggable={false} />
            </button>
          </div>
        </div>
      </div>
    </InverseScaleBox>
  );
};
